import { type ReactElement } from 'react';
import { Alert, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { Check, Pin, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { tileSpecFromChartPlan, tileSpecKey } from '../PinnedTileTypes';
import { usePinnedTilesStore } from '../usePinnedTiles';
import type { SurfaceAssistViewModel } from './SurfaceAssistViewModel';
import SurfaceAssistChart from './SurfaceAssistChart';

interface SurfaceAssistResultPanelProps {
    vm: SurfaceAssistViewModel;
    surfaceId: string;
    onDrill: (field: AnalyticsField, value: string) => void;
    /** Where a pinned chart lands: a tile card on the page (classic surfaces) or an interior-nav item (side-rail surfaces). */
    pinTarget?: 'page' | 'nav';
    /** Called after a chart is added to (or reopened from) the nav, with the tile id — the bar navigates to it. */
    onPinned?: (tileId: string) => void;
}

/** The vis overlay: a loading state, error, narrative + interactive chart (pinnable to the page or nav), or a "create a classification" offer. */
export default function SurfaceAssistResultPanel({ vm, surfaceId, onDrill, pinTarget = 'page', onPinned }: SurfaceAssistResultPanelProps): ReactElement {
    const navigate = useNavigate();
    const pinnedTiles = usePinnedTilesStore();

    if (vm.loading) {
        return (
            <Group gap="xs" py="sm" justify="center">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Thinking…
                </Text>
            </Group>
        );
    }
    if (vm.error !== null) {
        return (
            <Alert color="red" variant="light" title="Assist unavailable">
                {vm.error}
            </Alert>
        );
    }

    const plan = vm.plan;
    if (plan === null) return <></>;
    const existingTile =
        plan.workflow === 'chart' ? pinnedTiles.tilesFor(surfaceId).find((tile) => tileSpecKey(tile.spec) === tileSpecKey(tileSpecFromChartPlan(plan))) : undefined;
    const pinned = existingTile !== undefined;
    const toNav = pinTarget === 'nav';

    return (
        <Stack gap="sm">
            {vm.narrative.length > 0 && <Text size="sm">{vm.narrative}</Text>}

            {plan.workflow === 'chart' && (
                <>
                    <SurfaceAssistChart plan={plan} aggregate={vm.aggregate} trend={vm.trend} onDrill={onDrill} />
                    <Group justify="space-between" gap="xs" wrap="nowrap">
                        {plan.viz !== 'trend' ?
                            <Text size="xs" c="dimmed">
                                Click a {plan.viz === 'donut' ? 'slice' : 'bar'} to filter the page →
                            </Text>
                        :   <span />}
                        <Button
                            size="compact-xs"
                            variant={pinned ? 'light' : 'filled'}
                            color={pinned ? 'teal' : undefined}
                            leftSection={pinned ? <Check size={13} /> : <Pin size={13} />}
                            disabled={pinned && !toNav}
                            onClick={() => {
                                if (existingTile !== undefined) {
                                    if (toNav) onPinned?.(existingTile.id);
                                    return;
                                }
                                const tile = pinnedTiles.pin(surfaceId, tileSpecFromChartPlan(plan), 'ama');
                                if (toNav) onPinned?.(tile.id);
                            }}
                            style={{ flexShrink: 0 }}
                        >
                            {toNav ?
                                pinned ?
                                    'In nav — view'
                                :   'Add to nav'
                            : pinned ?
                                'Pinned to page'
                            :   'Pin to page'}
                        </Button>
                    </Group>
                </>
            )}

            {plan.workflow === 'propose-field' && (
                <Button
                    leftSection={<Wand2 size={15} />}
                    variant="light"
                    onClick={() => navigate(`/classification/${plan.category.toLowerCase()}?draft=${encodeURIComponent(plan.fieldDraftPrompt)}`)}
                >
                    Create a “{plan.missingConcept}” classification
                </Button>
            )}
        </Stack>
    );
}
